import React, { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Star } from "lucide-react";

function Stars({ value, onChange, size = 18 }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          onClick={() => onChange && onChange(n)}
          className={onChange ? "cursor-pointer" : "cursor-default"}
          aria-label={`Rate ${n}`}
          data-testid={onChange ? `review-star-${n}` : undefined}
        >
          <Star
            size={size}
            strokeWidth={1.2}
            className={n <= value ? "text-[#BCA37F]" : "text-[#BCA37F]/30"}
            fill={n <= value ? "#BCA37F" : "none"}
          />
        </button>
      ))}
    </div>
  );
}

export default function ProductReviews({ slug, productId }) {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ rating: 5, title: "", body: "", author_name: user?.name || "", author_email: user?.email || "", photos: [] });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/products/${slug}/reviews`);
      setData(data);
    } catch {}
  };
  useEffect(() => { load(); }, [slug]);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploadingPhoto(true);
    const uploaded = [];
    for (const f of Array.from(files).slice(0, 3 - form.photos.length)) {
      if (!/^image\//.test(f.type)) continue;
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(`/uploads/image?kind=review`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(`${BACKEND_URL}${data.url}`);
      } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    }
    setForm((f) => ({ ...f, photos: [...f.photos, ...uploaded] }));
    setUploadingPhoto(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.author_name.trim()) return toast.error("Please share your name.");
    if (form.body.trim().length < 10) return toast.error("Review must be at least 10 characters.");
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        product_id: productId,
        rating: form.rating,
        title: form.title,
        body: form.body,
        author_name: form.author_name,
        author_email: form.author_email || null,
        photos: form.photos,
      });
      toast.success("Thank you — your review is waiting on moderation.");
      setShowForm(false);
      setForm({ rating: 5, title: "", body: "", author_name: user?.name || "", author_email: user?.email || "", photos: [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit review.");
    } finally { setSubmitting(false); }
  };

  return (
    <section className="mt-24 border-t border-[#BCA37F]/25 pt-14" data-testid="pdp-reviews">
      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B]">Reviews</p>
          <h3 className="font-serif text-3xl md:text-4xl mt-2">
            {data.count > 0 ? (
              <>
                {data.average.toFixed(1)} · <span className="text-[#7A7A7A]">{data.count} review{data.count === 1 ? "" : "s"}</span>
              </>
            ) : "No reviews yet."}
          </h3>
          {data.count > 0 && <div className="mt-2"><Stars value={Math.round(data.average)} /></div>}
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="btn-editorial-ghost"
          data-testid="reviews-write"
        >
          {showForm ? "Cancel" : "Write a review"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-[#F3EFEA]/40 p-6 md:p-8 mb-10 space-y-5" data-testid="review-form">
          <div>
            <p className="text-[11px] uppercase tracking-nav text-[#7A7A7A] mb-2">Rating</p>
            <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} size={24} />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <label className="block"><span className="text-[11px] uppercase tracking-nav text-[#7A7A7A]">Your name</span>
              <input required className="input-underline" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} data-testid="review-name" /></label>
            <label className="block"><span className="text-[11px] uppercase tracking-nav text-[#7A7A7A]">Email (optional)</span>
              <input type="email" className="input-underline" value={form.author_email} onChange={(e) => setForm({ ...form, author_email: e.target.value })} data-testid="review-email" /></label>
          </div>
          <label className="block"><span className="text-[11px] uppercase tracking-nav text-[#7A7A7A]">Headline (optional)</span>
            <input className="input-underline" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="review-title" /></label>
          <label className="block"><span className="text-[11px] uppercase tracking-nav text-[#7A7A7A]">Your review</span>
            <textarea rows="4" required className="w-full bg-transparent border-b border-[#BCA37F]/50 focus:border-[#5C1A1B] outline-none py-2 text-sm" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="review-body" /></label>
          <div>
            <p className="text-[11px] uppercase tracking-nav text-[#7A7A7A] mb-2">Add photos (up to 3)</p>
            <label className="inline-block cursor-pointer bg-white border border-[#5C1A1B] text-[#5C1A1B] px-4 py-2 text-[11px] uppercase tracking-nav" data-testid="review-photo-label">
              {uploadingPhoto ? "Uploading…" : "Choose photos"}
              <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="review-photo-input" />
            </label>
            {form.photos.length > 0 && (
              <div className="mt-3 flex gap-2">
                {form.photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p} alt="" className="w-16 h-20 object-cover" />
                    <button type="button" onClick={() => setForm({ ...form, photos: form.photos.filter((_, j) => j !== i) })} className="absolute top-0 right-0 bg-black/70 text-white text-xs px-1" aria-label="Remove photo">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button type="submit" disabled={submitting} className="btn-editorial" data-testid="review-submit">
            {submitting ? "Sending…" : "Submit review"}
          </button>
        </form>
      )}

      {data.reviews.length > 0 && (
        <ul className="divide-y divide-[#BCA37F]/25">
          {data.reviews.map((r) => (
            <li key={r.review_id} className="py-6" data-testid={`review-item-${r.review_id}`}>
              <div className="flex items-center gap-3">
                <Stars value={r.rating} size={14} />
                <p className="font-serif text-lg">{r.title || r.author_name}</p>
              </div>
              <p className="text-[11px] uppercase tracking-nav text-[#7A7A7A] mt-1">by {r.author_name} · {r.created_at?.slice(0, 10)}</p>
              <p className="mt-3 text-sm text-[#1A1A1A] leading-relaxed">{r.body}</p>
              {r.photos?.length > 0 && (
                <div className="mt-3 flex gap-2">
                  {r.photos.map((p, i) => <img key={i} src={p} alt="" className="w-20 h-24 object-cover" loading="lazy" />)}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
